import { api } from './api';
import type {
  LinkedInGenerateRequest,
  LinkedInGenerateResponse,
  LinkedInPostsResponse,
  LinkedInPatchRequest,
  LinkedInPatchResponse,
  LinkedInPostsQuery,
  LinkedInPreviewPromptResponse,
  LinkedInGenerationLayersResponse,
  LinkedInPostQuellen,
  LinkedInSuggestHooksRequest,
  LinkedInSuggestHooksResponse,
  LinkedInHookOption,
  LinkedInHookLibraryResponse,
  LinkedInFillHookRequest,
  LinkedInFillHookResponse,
  LinkedInSuggestAnglesRequest,
  LinkedInSuggestAnglesResponse,
  LinkedInBranchRequest,
  LinkedInBranchResponse,
  LinkedInRewriteRequest,
  LinkedInRewriteResponse,
  LinkedInLaunchSuggestRequest,
  LinkedInLaunchSuggestResponse,
  LinkedInLaunchGenerateRequest,
  LinkedInLaunchGenerateResponse,
  LinkedInLinkAdvocateRequest,
  LinkedInPromptFragmentsResponse,
  LinkedInCockpitPreset,
  LinkedInBriefResponse,
  LinkedInWaehlenResponse,
  LinkedInErfolgResponse,
} from '../types/linkedin';

/** "Brief zeigen": nur das Material + die Kernaussage holen (ein Haiku-Aufruf, ~1 Cent),
 *  BEVOR die drei Sonnet-Kandidaten laufen. Kein Eintrag in content_generations. */
export async function buildLinkedInBrief(
  req: LinkedInGenerateRequest
): Promise<LinkedInBriefResponse> {
  return api.post<LinkedInBriefResponse>('/linkedin/brief', req);
}

export async function generateLinkedInPost(
  req: LinkedInGenerateRequest
): Promise<LinkedInGenerateResponse> {
  return api.post<LinkedInGenerateResponse>('/linkedin/generate', req);
}

/** Stufe D: blind einen der generierten Kandidaten waehlen -- deckt danach das Judge-Urteil auf. */
export async function waehleLinkedInKandidat(
  postId: number,
  kandidatIndex: number
): Promise<LinkedInWaehlenResponse> {
  return api.post<LinkedInWaehlenResponse>(`/linkedin/posts/${postId}/waehlen`, {
    kandidat_index: kandidatIndex,
  });
}

/** Stufe D: "keiner, neu" -- keiner der Kandidaten taugt. */
export async function lehneLinkedInKandidatenAb(
  postId: number,
  gruende: string[]
): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/linkedin/posts/${postId}/ablehnen`, { gruende });
}

/** Welche Prompt-Layer bei EINER Generierung gefeuert haben (Glass-Box-Manifest). */
export async function getLinkedInGenerationLayers(
  generationId: number
): Promise<LinkedInGenerationLayersResponse> {
  return api.get<LinkedInGenerationLayersResponse>(`/linkedin/generations/${generationId}/layers`);
}

/** Quellen-Fluss EINES Posts: Herkunft, Erklaerung, Saetze mit Herkunft + Funden. */
export async function getLinkedInPostQuellen(
  postId: number,
  technik = false
): Promise<LinkedInPostQuellen> {
  return api.get<LinkedInPostQuellen>(`/linkedin/posts/${postId}/quellen${technik ? '?technik=1' : ''}`);
}

export async function suggestLinkedInHooks(
  req: LinkedInSuggestHooksRequest
): Promise<LinkedInSuggestHooksResponse> {
  return api.post<LinkedInSuggestHooksResponse>('/linkedin/suggest-hooks', req);
}

/** Browse the raw hook skeletons. FREE — no AI call happens server-side. */
export async function browseHookLibrary(params: {
  strategy_id: number;
  category?: string | null;
  q?: string;
  offset?: number;
  limit?: number;
}): Promise<LinkedInHookLibraryResponse> {
  const qs = new URLSearchParams({ strategy_id: String(params.strategy_id) });
  if (params.category) qs.set('category', params.category);
  if (params.q) qs.set('q', params.q);
  if (params.offset) qs.set('offset', String(params.offset));
  if (params.limit) qs.set('limit', String(params.limit));
  return api.get<LinkedInHookLibraryResponse>(`/linkedin/hook-library?${qs.toString()}`);
}

/** Stored fills of one skeleton. Free. */
export async function listHookVariants(
  strategy_id: number, hook_id: string
): Promise<{ variants: LinkedInHookOption[] }> {
  const qs = new URLSearchParams({ strategy_id: String(strategy_id), hook_id });
  return api.get<{ variants: LinkedInHookOption[] }>(`/linkedin/hook-library/variants?${qs.toString()}`);
}

/** Fill ONE chosen skeleton. This is the only paid call in the library flow. */
export async function fillHookFromLibrary(
  req: LinkedInFillHookRequest
): Promise<LinkedInFillHookResponse> {
  return api.post<LinkedInFillHookResponse>('/linkedin/hook-library/fill', req);
}

export async function editHookVariant(
  cache_id: number, filled_text: string
): Promise<LinkedInHookOption> {
  return api.patch<LinkedInHookOption>(`/linkedin/hook-library/variants/${cache_id}`, { filled_text });
}

export async function deleteHookVariant(cache_id: number): Promise<{ ok: boolean }> {
  return api.delete<{ ok: boolean }>(`/linkedin/hook-library/variants/${cache_id}`);
}

export async function suggestLinkedInAngles(
  req: LinkedInSuggestAnglesRequest
): Promise<LinkedInSuggestAnglesResponse> {
  return api.post<LinkedInSuggestAnglesResponse>('/linkedin/suggest-angles', req);
}

export async function branchLinkedInPost(
  req: LinkedInBranchRequest
): Promise<LinkedInBranchResponse> {
  return api.post<LinkedInBranchResponse>('/linkedin/branch', req);
}

export async function rewriteLinkedInPost(
  req: LinkedInRewriteRequest
): Promise<LinkedInRewriteResponse> {
  return api.post<LinkedInRewriteResponse>('/linkedin/rewrite', req);
}

export async function previewLinkedInPrompt(
  req: LinkedInGenerateRequest
): Promise<LinkedInPreviewPromptResponse> {
  return api.post<LinkedInPreviewPromptResponse>('/linkedin/preview-prompt', req);
}

export async function fetchLinkedInPosts(
  query?: LinkedInPostsQuery
): Promise<LinkedInPostsResponse> {
  const params = new URLSearchParams();
  if (query?.strategy_id !== undefined) params.set('strategy_id', String(query.strategy_id));
  if (query?.status !== undefined) params.set('status', query.status);
  if (query?.limit !== undefined) params.set('limit', String(query.limit));
  const qs = params.toString();
  return api.get<LinkedInPostsResponse>(`/linkedin/posts${qs ? `?${qs}` : ''}`);
}

export async function patchLinkedInPost(
  id: number,
  patch: LinkedInPatchRequest
): Promise<LinkedInPatchResponse> {
  return api.patch<LinkedInPatchResponse>(`/linkedin/posts/${id}`, patch);
}

/** Stufe E -- Erfolgs-Buch: Zaehlung je Aufbau/Quelle/Judge-Kriterium. */
export async function fetchLinkedInErfolg(strategyId?: number): Promise<LinkedInErfolgResponse> {
  const qs = strategyId !== undefined ? `?strategy_id=${strategyId}` : '';
  return api.get<LinkedInErfolgResponse>(`/linkedin/erfolg${qs}`);
}

// --- Launch sequence (page cold-start) ---

export async function suggestLaunchSequence(
  req: LinkedInLaunchSuggestRequest
): Promise<LinkedInLaunchSuggestResponse> {
  return api.post<LinkedInLaunchSuggestResponse>('/linkedin/launch/suggest', req);
}

export async function generateLaunchSequence(
  req: LinkedInLaunchGenerateRequest
): Promise<LinkedInLaunchGenerateResponse> {
  return api.post<LinkedInLaunchGenerateResponse>('/linkedin/launch/generate', req);
}

export async function linkLaunchAdvocate(
  req: LinkedInLinkAdvocateRequest
): Promise<unknown> {
  return api.post<unknown>('/linkedin/launch/link', req);
}

// --- Prompt-Bausteine -------------------------------------------------------
export async function fetchLinkedInPromptFragments(): Promise<LinkedInPromptFragmentsResponse> {
  return api.get<LinkedInPromptFragmentsResponse>('/linkedin/prompt-fragments');
}

export async function saveLinkedInPromptFragment(key: string, text: string) {
  return api.put<{ key: string; override_text: string; is_overridden: boolean }>(
    `/linkedin/prompt-fragments/${encodeURIComponent(key)}`,
    { text }
  );
}

export async function resetLinkedInPromptFragment(key: string) {
  return api.delete<{ key: string; is_overridden: boolean }>(
    `/linkedin/prompt-fragments/${encodeURIComponent(key)}`
  );
}

// --- Cockpit-Presets --------------------------------------------------------
export async function fetchLinkedInPresets(strategyId: number): Promise<{ presets: LinkedInCockpitPreset[] }> {
  return api.get<{ presets: LinkedInCockpitPreset[] }>(`/linkedin/presets/${strategyId}`);
}

export async function createLinkedInPreset(
  strategyId: number,
  name: string,
  config: Record<string, unknown>
): Promise<LinkedInCockpitPreset> {
  return api.post<LinkedInCockpitPreset>(`/linkedin/presets/${strategyId}`, { name, config });
}

export async function updateLinkedInPreset(
  strategyId: number,
  presetId: number,
  patch: { name?: string; config?: Record<string, unknown> }
): Promise<LinkedInCockpitPreset> {
  return api.patch<LinkedInCockpitPreset>(`/linkedin/presets/${strategyId}/${presetId}`, patch);
}

export async function deleteLinkedInPreset(strategyId: number, presetId: number) {
  return api.delete<{ deleted: boolean }>(`/linkedin/presets/${strategyId}/${presetId}`);
}
